import { TbMessage2Question } from "react-icons/tb";
import { GoInfo } from "react-icons/go";
import { Button } from "react-bootstrap";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../AppContext";
import './PuzzleQuestion.scss';

export default function PuzzleQuestion({ quiz, setQuiz, goNext }) {
    const app = useContext(AppContext);
    const { t } = useTranslation();

    const zones = quiz.question.meta ?? [];
    const zoneCount = zones.length;

    const [droppedNumbers, setDroppedNumbers] = useState({});
    const [dragging, setDragging] = useState(null);

    useEffect(() => {
        setDroppedNumbers({});
        setDragging(null);
    }, [quiz.questionIdx]);

    const noImage = !quiz.question.image_uri;

    const onDrop = (zoneIdx) => {
        if (dragging === null) return;

        setDroppedNumbers((prev) => ({
            ...prev,
            [zoneIdx]: dragging,
        }));

        setDragging(null);
    };

    const isCorrect = () => {
        if (Object.keys(droppedNumbers).length !== zoneCount) return false;

        for (let i = 0; i < zoneCount; i++) {
            if (droppedNumbers[i] !== i + 1) return false;
        }
        return true;
    };

    const submitAnswer = () => {
        let q = { ...quiz };
        const correct = isCorrect();

        correct ? app.playSfxCorrect() : app.playSfxWrong();

        q.lastResult = correct ? 1 : 0;
        q.results[quiz.questionIdx] = correct ? 1 : 0;

        if (!correct) {
            q.perfect = false;
            q.questions.push(q.questions[q.questionIdx]);
        }

        setQuiz({ ...q });
    };

    const resetPuzzle = () => {
        setDroppedNumbers({});
        setDragging(null);
    };

    return (
        <div className={"quiz-question puzzle " + (noImage ? "no-image" : "")}>
            <div className="q-image puzzle-image-wrapper">
                {!noImage && (
                    <div className="puzzle-image-container">
                        <img src={quiz.question.image_uri} alt="" />

                        {/* ZONES */}
                        {zones.map((z, idx) => (
                            <div
                                key={idx}
                                className="puzzle-zone"
                                style={{
                                    top: z.top,
                                    left: z.left,
                                    width: z.size,
                                    paddingTop: z.size,
                                    "--zone-size": z.size,
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => onDrop(idx)}
                            >
                                {droppedNumbers[idx] && (
                                    <div className="puzzle-number locked">
                                        {droppedNumbers[idx]}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {noImage && <TbMessage2Question className="placeholder-image" />}
            </div>

            <div className="q-details">
                <ul className="q-answers">
                    {/* DRAGGABLE NUMBERS */}
                    <li>
                        <div className="puzzle-numbers">
                            {Array.from({ length: zoneCount }, (_, i) => i + 1).map(
                                (n) => (
                                    <div
                                        key={n}
                                        className={
                                            "puzzle-number " +
                                            (Object.values(droppedNumbers).includes(n)
                                                ? "used"
                                                : "")
                                        }
                                        draggable={quiz.lastResult === -1}
                                        onDragStart={() => setDragging(n)}
                                    >
                                        {n}
                                    </div>
                                )
                            )}
                        </div>
                    </li>

                    {quiz.lastResult === -1 && (
                        <li>
                            <Button
                                variant="secondary"
                                disabled={Object.keys(droppedNumbers).length === 0}
                                onClick={resetPuzzle}
                            >
                                {t("button.reset")}
                            </Button>
                            <p className="small text-muted mt-5">
                                <GoInfo style={{ fontSize: 16 }} />{" "}
                                {t("form.dragNumbers")}
                            </p>
                            <Button variant="primary" onClick={submitAnswer}>{t('button.submitAnswer')}</Button>
                        </li>
                    )}

                    {quiz.lastResult !== -1 && (
                        <li>
                            <h4
                                className={
                                    quiz.lastResult === 1
                                        ? "text-success fw-bold"
                                        : "text-danger fw-bold"
                                }
                            >
                                {quiz.lastResult === 1 ? t("good") : t("wrong")}
                            </h4>
                            <p
                                className="small text-muted"
                                dangerouslySetInnerHTML={{
                                    __html: quiz.question.explanation,
                                }}
                            />
                            <Button variant="primary" onClick={goNext}>
                                {t("button.next")}
                            </Button>
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
}